import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '@/services/authService';
import { setAuthToken, clearAuthToken, getAuthToken, isTokenExpired } from '@/api/client';
import { DEFAULT_ROLE_PERMISSIONS } from '@/constants';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize session on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('pms_user');
      const storedToken = localStorage.getItem('pms_token');

      // If token missing or expired, invalidate immediately
      if (!storedUser || !storedToken || isTokenExpired(storedToken)) {
        clearAuthToken();
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);
        setAuthToken(storedToken);
        setUser(parsedUser);

        // Verify session freshness with server
        try {
          const freshUser = await authService.getMe(storedToken);
          if (freshUser) {
            const mergedUser = { ...parsedUser, ...freshUser };
            localStorage.setItem('pms_user', JSON.stringify(mergedUser));
            setUser(mergedUser);
          }
        } catch (err) {
          if (err.response?.status === 401) {
            clearAuthToken();
            setUser(null);
          }
        }
      } catch (e) {
        clearAuthToken();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Main login handler (supports both Mock and Live mode)
  const login = useCallback(async (usernameOrEmail, password, hospitalId) => {
    const result = await authService.login(usernameOrEmail, password, hospitalId);
    const { user: authUser, accessToken } = result;

    if (accessToken) {
      setAuthToken(accessToken);
    }
    if (authUser) {
      localStorage.setItem('pms_user', JSON.stringify(authUser));
      setUser(authUser);
    }

    return authUser;
  }, []);

  // Logout handler
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      clearAuthToken();
      setUser(null);
    }
  }, []);

  // Update user profile fields in session
  const updateUser = useCallback((updatedFields) => {
    setUser((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, ...updatedFields };
      localStorage.setItem('pms_user', JSON.stringify(merged));
      return merged;
    });
  }, []);

  // Derived normalized user roles
  const userRoles = React.useMemo(() => {
    if (!user) return [];
    if (Array.isArray(user.roles) && user.roles.length > 0) {
      return user.roles.map((r) => String(r).toUpperCase().trim());
    }
    if (user.role) {
      return [String(user.role).toUpperCase().trim()];
    }
    return [];
  }, [user]);

  // Derived effective user permissions (merges backend permissions with role fallback matrix)
  const userPermissions = React.useMemo(() => {
    if (!user) return [];
    
    // Check if backend supplied explicit permissions
    const backendPerms = Array.isArray(user.permissions)
      ? user.permissions.map((p) => String(p).toUpperCase().trim())
      : [];

    // Fallback mapping from roles
    const derivedFromRoles = new Set(backendPerms);

    userRoles.forEach((role) => {
      const permsForRole = DEFAULT_ROLE_PERMISSIONS[role] || [];
      permsForRole.forEach((p) => derivedFromRoles.add(p));

      // Handle common variations
      if (['ADMIN', 'SUPER_ADMIN', 'PLATFORM_ADMIN', 'HOSPITAL_ADMIN'].includes(role)) {
        derivedFromRoles.add('PLATFORM_MANAGE');
        derivedFromRoles.add('*');
      }
    });

    return Array.from(derivedFromRoles);
  }, [user, userRoles]);

  // Check if user has specific permission
  const hasPermission = useCallback(
    (permissionKey) => {
      if (!user) return false;
      if (!permissionKey) return true;

      // Super admin or wildcard PLATFORM_MANAGE has access to everything
      if (
        userPermissions.includes('*') ||
        userPermissions.includes('PLATFORM_MANAGE') ||
        userRoles.includes('SUPER_ADMIN') ||
        userRoles.includes('ADMIN') ||
        userRoles.includes('HOSPITAL_ADMIN')
      ) {
        return true;
      }

      const normKey = String(permissionKey).toUpperCase().trim();
      return userPermissions.includes(normKey);
    },
    [user, userPermissions, userRoles]
  );

  // Check if user has ANY of the specified permissions
  const hasAnyPermission = useCallback(
    (permissionKeys = []) => {
      if (!user) return false;
      if (!permissionKeys || permissionKeys.length === 0) return true;
      return permissionKeys.some((key) => hasPermission(key));
    },
    [user, hasPermission]
  );

  // Check if user has ALL of the specified permissions
  const hasAllPermissions = useCallback(
    (permissionKeys = []) => {
      if (!user) return false;
      if (!permissionKeys || permissionKeys.length === 0) return true;
      return permissionKeys.every((key) => hasPermission(key));
    },
    [user, hasPermission]
  );

  // Check if user has a required role (normalizing multi-role arrays)
  const hasRole = useCallback(
    (roles) => {
      if (!user || userRoles.length === 0) return false;

      const roleList = (Array.isArray(roles) ? roles : [roles]).map((r) =>
        String(r).toUpperCase().trim()
      );

      const hasAdminSubtype = userRoles.some((r) =>
        ['ADMIN', 'SUPER_ADMIN', 'PLATFORM_ADMIN', 'HOSPITAL_ADMIN', 'CLINIC_ADMIN'].includes(r)
      );

      return roleList.some((requiredRole) => {
        if (requiredRole === 'ADMIN' && hasAdminSubtype) return true;
        return userRoles.includes(requiredRole);
      });
    },
    [user, userRoles]
  );

  // Check if user has access to a clinic
  const hasClinicAccess = useCallback(
    (clinicId) => {
      if (!user) return false;
      if (hasPermission('CLINIC_ALL_VIEW') || hasPermission('PLATFORM_MANAGE')) return true;
      if (user.clinicId === clinicId) return true;
      if (Array.isArray(user.assignedClinics) && user.assignedClinics.includes(clinicId)) return true;
      return false;
    },
    [user, hasPermission]
  );

  const value = React.useMemo(
    () => ({
      user,
      loading,
      login,
      loginWithMock: login, // Backward compatibility alias
      logout,
      getToken: getAuthToken,
      userRoles,
      userPermissions,
      hasRole,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasClinicAccess,
      updateUser,
      isAuthenticated: !!user,
    }),
    [
      user,
      loading,
      login,
      logout,
      updateUser,
      userRoles,
      userPermissions,
      hasRole,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasClinicAccess,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
